import axios from 'axios'
import {getAdminToken} from '../auth/_requests'

const API_URL = process.env.REACT_APP_API_URL
const adminApi = axios.create({withCredentials: true})
adminApi.interceptors.request.use((config) => {
  const token = getAdminToken()
  if (token && config.headers) config.headers.Authorization = `Bearer ${token}`
  return config
})

const base = `${API_URL}/admin/sms-campaigns`

export interface SmsStep {
  key: string
  label: string
  day_offset: number
  text: string
  /** > 0 = follow-up: sent this many minutes after the previous step, if the contact hasn't opted out. */
  delay_minutes: number
}
export interface SmsLink {
  token: string
  label: string
  url: string
}
export interface SmsCounts {
  pending: number
  active: number
  done: number
  stopped: number
  invalid: number
  total: number
}
export interface SmsCampaign {
  id: string
  name: string
  description: string
  active: boolean
  steps: SmsStep[]
  links: SmsLink[]
  send_window: {start_hour: number; end_hour: number}
  skip_shabbat: boolean
  daily_cap: number
  max_catchup_days: number
  /** Sender id override; '' = use default_sender (the MULTISEND_FROM env). */
  sender: string
  default_sender: string
  test_phones: string[]
  counts: SmsCounts
  created_at: string | null
}
export interface SmsContact {
  id: string
  phone: string
  name: string
  status: string
  enrolled_at: string | null
  sent_count: number
  click_count: number
  last_sent_at: string | null
  steps_done: number
}
export interface ScheduleDay {
  date: string
  total: number
  steps: {key: string; label: string; day_offset: number; count: number}[]
}
export interface ScheduleData {
  ok: boolean
  today: string
  daily_cap: number
  pending: number
  blocked_reason: string
  steps: {key: string; label: string; day_offset: number}[]
  days: ScheduleDay[]
}
export interface ClickRow {
  message_id: string
  name: string
  phone: string
  is_test: boolean
  step_key: string
  step_label: string
  day_offset: number
  token: string
  url: string
  clicks: number
  first_click_at: string
  last_click_at: string
  user_agent: string
}
export interface StatRow {
  key: string
  label: string
  day_offset: number
  sent: number
  failed: number
  clicked: number
  total_clicks: number
  ctr: number
}
export interface MessageRow {
  id: string
  phone: string
  name: string
  step_key: string
  step_label: string
  day_offset: number
  text: string
  status: string
  error: string
  is_test: boolean
  created_at: string
  clicks: number
}

export function listCampaigns(): Promise<{
  data: {ok: boolean; sms_configured: boolean; link_base: string; campaigns: SmsCampaign[]}
}> {
  return adminApi.get(base)
}
export function createCampaign(data: {name: string; description?: string}): Promise<{data: {ok: boolean; campaign: SmsCampaign}}> {
  return adminApi.post(base, data)
}
export function getCampaign(id: string): Promise<{data: {ok: boolean; campaign: SmsCampaign; link_base: string; sms_configured: boolean}}> {
  return adminApi.get(`${base}/${id}`)
}
export function updateCampaign(id: string, data: Partial<SmsCampaign>): Promise<{data: {ok: boolean; campaign: SmsCampaign}}> {
  return adminApi.put(`${base}/${id}`, data)
}
export function deleteCampaign(id: string): Promise<{data: {ok: boolean}}> {
  return adminApi.delete(`${base}/${id}`)
}
export function saveSteps(id: string, steps: SmsStep[]): Promise<{data: {ok: boolean; campaign: SmsCampaign}}> {
  return adminApi.put(`${base}/${id}/steps`, {steps})
}
export function importContacts(
  id: string,
  rows: {phone: string; name?: string}[],
  source: string
): Promise<{
  data: {ok: boolean; added: number; duplicates: number; invalid: number; opted_out: number; invalid_samples: string[]; counts: SmsCounts}
}> {
  return adminApi.post(`${base}/${id}/contacts/import`, {rows, source})
}
export interface OptOutRow {
  phone: string
  name: string
  source: string
  campaign: string
  created_at: string
}
export function getOptOuts(): Promise<{data: {ok: boolean; total: number; opt_outs: OptOutRow[]}}> {
  return adminApi.get(`${base}/opt-outs`)
}
export function removeOptOut(phone: string): Promise<{data: {ok: boolean}}> {
  return adminApi.delete(`${base}/opt-outs/${encodeURIComponent(phone)}`)
}
export function getContacts(
  id: string,
  params: {status?: string; search?: string; page?: number; limit?: number}
): Promise<{data: {ok: boolean; total: number; page: number; contacts: SmsContact[]}}> {
  return adminApi.get(`${base}/${id}/contacts`, {params})
}
export function addContact(id: string, data: {phone: string; name?: string}): Promise<{data: {ok: boolean}}> {
  return adminApi.post(`${base}/${id}/contacts`, data)
}
export function deleteContact(id: string, cid: string): Promise<{data: {ok: boolean}}> {
  return adminApi.delete(`${base}/${id}/contacts/${cid}`)
}
export function stopContact(id: string, cid: string): Promise<{data: {ok: boolean}}> {
  return adminApi.post(`${base}/${id}/contacts/${cid}/stop`)
}
/** Send one step to one contact immediately. Omit step_key for the next unsent step. */
export function sendToContact(
  id: string,
  cid: string,
  stepKey?: string
): Promise<{data: {ok: boolean; error?: string; step?: SmsStep; text?: string; enrolled?: boolean; done?: boolean}}> {
  return adminApi.post(`${base}/${id}/contacts/${cid}/send`, stepKey ? {step_key: stepKey} : {})
}
export function releaseContacts(id: string, count: number): Promise<{data: {ok: boolean; released: number; counts: SmsCounts}}> {
  return adminApi.post(`${base}/${id}/release`, {count})
}
export function getSchedule(id: string, days = 14): Promise<{data: ScheduleData}> {
  return adminApi.get(`${base}/${id}/schedule`, {params: {days}})
}
export function addTestPhone(id: string, phone: string): Promise<{data: {ok: boolean; test_phones: string[]}}> {
  return adminApi.post(`${base}/${id}/test-phones`, {phone})
}
export function removeTestPhone(id: string, phone: string): Promise<{data: {ok: boolean; test_phones: string[]}}> {
  return adminApi.delete(`${base}/${id}/test-phones/${encodeURIComponent(phone)}`)
}
export function sendTest(
  id: string,
  data: {step_key?: string; text?: string; phones?: string[]; name?: string}
): Promise<{data: {ok: boolean; results: {phone: string; ok: boolean; error: string; text: string}[]}}> {
  return adminApi.post(`${base}/${id}/test`, data)
}
export function runNow(id: string, force = false): Promise<{data: {ok: boolean; result: any}}> {
  return adminApi.post(`${base}/${id}/run-now`, {force})
}
export function getMessages(
  id: string,
  params: {step_key?: string; status?: string; clicked?: string; include_tests?: string; limit?: number}
): Promise<{data: {ok: boolean; messages: MessageRow[]}}> {
  return adminApi.get(`${base}/${id}/messages`, {params})
}
export function getClicks(id: string): Promise<{data: {ok: boolean; clicks: ClickRow[]}}> {
  return adminApi.get(`${base}/${id}/clicks`)
}
export function getStats(id: string): Promise<{data: {ok: boolean; steps: StatRow[]}}> {
  return adminApi.get(`${base}/${id}/stats`)
}
